/**
 * NIGHTBOUND - shared UI primitives.
 *
 * Deliberately small and unopinionated. Every status communicates with text as
 * well as colour, and every interactive control is a real button with an
 * accessible name.
 */

import {
  forwardRef,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
} from 'react'

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ')
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'quiet'
export type ButtonSize = 'sm' | 'md' | 'lg'

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'border-ember-600/70 bg-ember-500/15 text-ember-400 hover:bg-ember-500/25 hover:text-ember-400 active:bg-ember-500/30',
  secondary:
    'border-ash-600 bg-night-700/70 text-bone-300 hover:bg-night-600 hover:text-bone-200',
  ghost: 'border-transparent text-bone-400 hover:bg-night-700/60 hover:text-bone-200',
  quiet: 'border-ash-700/50 bg-transparent text-ash-300 hover:bg-night-700/40 hover:text-bone-300',
  danger: 'border-blood-500/70 bg-blood-700/40 text-blood-400 hover:bg-blood-600/50 hover:text-bone-200',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-[11px] tracking-[0.12em]',
  md: 'px-4 py-2 text-xs tracking-[0.14em]',
  lg: 'px-6 py-3 text-sm tracking-[0.16em]',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', fullWidth, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        'inline-flex select-none items-center justify-center gap-2 rounded-[3px] border font-semibold uppercase transition-colors disabled:cursor-not-allowed disabled:opacity-40',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    />
  )
})

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export interface PanelProps extends HTMLAttributes<HTMLDivElement> {
  inset?: boolean
  grain?: boolean
}

export function Panel({ inset, grain, className, children, ...rest }: PanelProps) {
  return (
    <div className={cx('nb-panel', inset && 'nb-panel--inset', grain && 'nb-grain', className)} {...rest}>
      {children}
    </div>
  )
}

export function PanelHeading({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-ash-700/40 px-3 py-2">
      <h2 className="nb-label text-bone-300">{children}</h2>
      {right}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Need / stat bars
// ---------------------------------------------------------------------------

export interface MeterProps {
  label: string
  value: number
  max?: number
  color: string
  icon?: ReactNode
  /** Below this value the meter is announced as low (never colour-only). */
  lowAt?: number
  showValue?: boolean
  compact?: boolean
}

export function Meter({
  label,
  value,
  max = 100,
  color,
  icon,
  lowAt,
  showValue = true,
  compact,
}: MeterProps) {
  const clamped = Math.max(0, Math.min(max, value))
  const percent = max > 0 ? (clamped / max) * 100 : 0
  const low = lowAt !== undefined && clamped < lowAt

  return (
    <div className={compact ? 'space-y-0.5' : 'space-y-1'}>
      <div className="flex items-center justify-between gap-2 leading-none">
        <span className="nb-label flex items-center gap-1.5 text-[10px]">
          {icon ? (
            <span aria-hidden className="text-[11px]">
              {icon}
            </span>
          ) : null}
          {label}
        </span>
        {showValue ? (
          <span
            className={cx(
              'nb-numeric text-[11px]',
              low ? 'text-blood-400' : 'text-bone-300',
            )}
          >
            {Math.round(clamped)}
            {low ? <span className="ml-1 font-semibold uppercase">low</span> : null}
          </span>
        ) : null}
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={max}
        className={cx(
          'w-full overflow-hidden rounded-full border border-ash-700/50 bg-night-950/80',
          compact ? 'h-1.5' : 'h-2',
        )}
      >
        <div
          className="h-full rounded-full transition-[width] duration-700 ease-out"
          style={{
            width: `${percent}%`,
            background: `linear-gradient(90deg, ${color}99, ${color})`,
            boxShadow: `0 0 8px ${color}44`,
          }}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

export function Chip({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode
  tone?: 'neutral' | 'good' | 'warn' | 'bad'
  title?: string
}) {
  const tones = {
    neutral: 'border-ash-600/60 text-ash-300',
    good: 'border-rot-600/60 text-rot-500',
    warn: 'border-ember-600/60 text-ember-500',
    bad: 'border-blood-500/60 text-blood-400',
  } as const
  return (
    <span
      title={title}
      className={cx(
        'inline-flex items-center gap-1 rounded-full border bg-night-950/50 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em]',
        tones[tone],
      )}
    >
      {children}
    </span>
  )
}

export function Divider({ className }: { className?: string }) {
  return <div className={cx('h-px w-full bg-gradient-to-r from-transparent via-ash-700/60 to-transparent', className)} />
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span role="status" aria-live="polite" className="inline-flex items-center gap-2 text-ash-300">
      <span
        aria-hidden
        className="h-3 w-3 animate-spin rounded-full border border-ash-500 border-t-ember-500"
      />
      <span className="nb-label">{label}</span>
    </span>
  )
}
